import { Navigate, Outlet, useLocation } from "react-router-dom";
import { usePermissions } from "../../contexts/PermissionContext";
import { useStateContext } from "../../contexts/ContextProvider";
import { getRequiredPermission, findFirstAccessibleRoute, FALLBACK_ROUTE } from "../../config/routePermissions";
import { StaffAccess, canAccess } from "../layouts/navigation/staffAccess";

/**
 * Composant de garde qui vérifie les permissions pour les routes.
 * - Attend que l'utilisateur soit chargé avant de vérifier
 * - Super admin a accès à tout
 * - Routes avec permission null sont réservées aux super-admins
 * - Redirige vers la première route accessible si pas de permission
 */
export default function RequirePermission() {
    const location = useLocation();
    const { hasPermission, hasAnyPermission, hasRole, isSuperAdmin, isAdmin } = usePermissions();
    const { isUserLoading, user, currentFacility } = useStateContext();

    // Attendre que l'utilisateur soit chargé avant de vérifier les permissions
    if (isUserLoading || user.id === 0) {
        return null; // On ne rend rien pendant le chargement
    }

    // Super admin a accès à tout. NE PAS ouvrir les routes sur la seule
    // appartenance à une facility groupe : un staff à rôle restreint du groupe
    // doit passer par ses permissions réelles.
    if (isSuperAdmin) {
        return <Outlet />;
    }

    // Trouver la permission requise pour cette route
    const requiredPermission = getRequiredPermission(location.pathname);

    // Route réservée aux super-admins (permission === null)
    if (requiredPermission === null) {
        return <Navigate to={getFirstAccessibleRoute()} replace />;
    }

    // Routes CRM internes (_crm:any, _crm:prospects, _crm:clients). Évaluées AVANT
    // le passe-droit des admins : un admin d'établissement client n'a rien à faire
    // dans le CRM interne de TriggerFlow, seuls ses drapeaux CRM l'y autorisent.
    if (typeof requiredPermission === 'string' && requiredPermission.startsWith('_crm:')) {
        // Même règle que le menu (`routePermissionFor` produit ces clés).
        if (!canAccess(user, requiredPermission.slice(1) as StaffAccess)) {
            return <Navigate to={getFirstAccessibleRoute()} replace />;
        }
        return <Outlet />;
    }

    // Admin d'établissement : accès à toutes les routes client.
    if (isAdmin) {
        return <Outlet />;
    }

    // Vérifier si l'utilisateur a la permission requise
    if (requiredPermission !== undefined) {
        const hasAccess = checkPermission(requiredPermission);
        if (!hasAccess) {
            return <Navigate to={getFirstAccessibleRoute()} replace />;
        }
    }

    // Permission accordée ou pas de permission requise
    return <Outlet />;

    /**
     * Vérifie si l'utilisateur a la permission (simple ou multiple)
     */
    function checkPermission(perm: string | string[]): boolean {
        if (Array.isArray(perm)) {
            return hasAnyPermission(perm);
        }
        return hasPermission(perm);
    }

    /**
     * Trouve la première route accessible pour l'utilisateur
     */
    function getFirstAccessibleRoute(): string {
        // Agent d'entretien : sa page d'accueil est "Mes chambres" (cohérent avec
        // SmartRedirect). Évite une boucle de redirection vers /settings (inaccessible)
        // si l'agent atteint une route bloquée comme /housekeeping/maintenance.
        if (hasRole('housekeeping-agent')) {
            return '/housekeeping/my-rooms';
        }
        if (hasRole('housekeeping-technician')) {
            return '/housekeeping/maintenance';
        }

        const route = findFirstAccessibleRoute(currentFacility, checkPermission);
        if (route) {
            return route;
        }

        return FALLBACK_ROUTE;
    }
}
